/* TopNavi 2.0 — Airtable Product Page behavior
   Vanilla JS, defer loaded only on product templates from layout/theme.liquid.
   Functions:
     - Pro Selector variant cards (click → Shopify form, image, price)
     - Quantity selector
     - Image gallery (Swiper)
     - Upgrade popup (open/close, accessory toggle with RCA→Mic dep, warranty)
     - Bundle running total
     - Multi-currency formatting (HUF/EUR/RON/PLN)
*/
(function () {
  'use strict';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initProductScope();
  }

  function initProductScope() {
    const root = document.querySelector('[data-ap-root]');
    if (!root) return;

    let config = {};
    const cfgScript = root.querySelector('[data-ap-config]');
    if (cfgScript) {
      try {
        config = JSON.parse(cfgScript.textContent || '{}');
      } catch (e) {
        config = {};
      }
    }

    // Non-Swiper-dependent inits run immediately.
    initQuantity(root);
    initStickyPrice(root);
    initVariantSelector(root, config);
    initDealPanel(root);
    initCofidisAndDelivery(root, config);
    initGiftActivation(root);

    // Új natív CSS scroll-snap galéria — nem kell Swiper várás
    initNativeGallery(root);

    // Kattintható nagyítás a fő képre — lightbox-szerű
    initMainImageZoom(root);

    // Variant URL preselect — a galéria most már él
    preselectVariantFromUrl(root, config);
  }

  /* ---------------------------------------------------------------------
     MODERN VARIANT SELECTOR — saját kártya-vezérlés, anti-flicker, popup szinkron
     --------------------------------------------------------------------- */
  function initVariantSelector(root, config) {
    const cards = root.querySelectorAll('[data-ap-variant-card]');
    if (!cards.length) {
      // Single-variant termék — semmi teendő a kártyákkal,
      // de a kompat-shim-et akkor is szinkronban kell tartani.
      return;
    }
    const idInput = root.querySelector('[data-ap-variant-id]');
    const priceEl = root.querySelector('[data-ap-price]');
    const compareEl = root.querySelector('[data-ap-price-compare]');
    const discountEl = root.querySelector('[data-ap-price-discount]');
    const priceRowEl = root.querySelector('[data-ap-price-row]');
    const saveWrapEl = root.querySelector('[data-ap-price-save]');
    const saveAmountEl = root.querySelector('[data-ap-price-save-amount]');
    const inventoryEl = root.querySelector('[data-ap-inventory]');
    const stickyPriceWrap = root.querySelector('[data-ap-sticky-price]');
    const stickyPriceStrong = root.querySelector('[data-ap-sticky-price] strong');
    const atcBtn = root.querySelector('[data-ap-atc]');
    const atcLabel = atcBtn && atcBtn.querySelector('span');
    const selectedDiv = document.getElementById('selected-variant-id');

    const variants = (config.variants) || [];
    const byId = {};
    variants.forEach(v => { byId[v.id] = v; });

    const t = (config.t) || {};

    function selectVariant(id, opts) {
      const v = byId[id];
      if (!v) return;
      opts = opts || {};

      // Kártya UI
      cards.forEach(c => {
        const matches = parseInt(c.getAttribute('data-variant-id'), 10) === id;
        c.classList.toggle('is-selected', matches);
        c.setAttribute('aria-pressed', matches ? 'true' : 'false');
      });

      // Form input value (a Shopify form submit ezt használja)
      if (idInput) idInput.value = id;

      // Ár
      if (priceEl) priceEl.textContent = formatMoney(v.price, config.currency);

      // Compare price + discount chip + megtakarítás-chip + is-sale állapot
      // (2026-07-23 akciós ár redesign — a chipek a price-row 2. sorában)
      // review-fix: Math.floor + U+2212 minusz — így a JS PONTOSAN azt írja,
      // amit a Liquid SSR (divided_by = csonkolás, −{{ pct }}%), nincs
      // "−14% → -15%" ugrás betöltéskor. 1% alatti kedvezménynél nincs chip.
      const onSale = !!(v.compare_at_price && v.compare_at_price > v.price);
      const salePct = onSale ? Math.floor((1 - v.price / v.compare_at_price) * 100) : 0;
      if (compareEl) {
        if (onSale) {
          compareEl.textContent = formatMoney(v.compare_at_price, config.currency);
          compareEl.style.display = '';
          if (discountEl) {
            if (salePct > 0) {
              discountEl.textContent = '−' + salePct + '%';
              discountEl.style.display = '';
            } else {
              discountEl.style.display = 'none';
            }
          }
        } else {
          compareEl.style.display = 'none';
          if (discountEl) discountEl.style.display = 'none';
        }
      }
      if (saveWrapEl) {
        if (onSale) {
          if (saveAmountEl) saveAmountEl.textContent = formatMoney(v.compare_at_price - v.price, config.currency);
          saveWrapEl.hidden = false;
        } else {
          saveWrapEl.hidden = true;
        }
      }
      if (priceRowEl) priceRowEl.classList.toggle('is-sale', onSale);
      if (stickyPriceWrap) stickyPriceWrap.classList.toggle('is-sale', onSale);
      // Galéria bal felső −% badge — mindig az AKTUÁLIS variáns kedvezménye
      const galleryBadge = root.querySelector('[data-ap-gallery-sale]');
      if (galleryBadge) {
        if (onSale && salePct > 0) {
          galleryBadge.textContent = '−' + salePct + '%';
          galleryBadge.hidden = false;
        } else {
          galleryBadge.hidden = true;
        }
      }

      // Raktár-állapot — háttérszín-osztály + rejtett legacy szöveg
      // - in:  helyi raktáron (qty > 0) → zöld, INGYEN SZÁLLÍTÁS headline
      // - low: külső raktár (available, qty ≤ 0) → sárga
      // - out: nem elérhető → piros
      const stockCard = root.querySelector('[data-ap-info-stock]');
      const stockLabel = root.querySelector('[data-ap-info-stock-label]');
      const stockTextEl = root.querySelector('[data-ap-stock-text]');
      let legacyText, headlineText, stockChipText, stockKlass;
      if (!v.available) {
        stockKlass = 'ap-inventory--out';
        legacyText = 'Jelenleg nem elérhető';
        headlineText = 'Jelenleg nem elérhető';
        stockChipText = '—';
      } else if (v.inventory_quantity > 0) {
        stockKlass = 'ap-inventory--in';
        legacyText = t.in_stock || 'Raktáron';
        headlineText = 'INGYEN SZÁLLÍTÁS';
        stockChipText = 'Raktáron';
      } else {
        stockKlass = 'ap-inventory--low';
        legacyText = 'Külső raktár';
        headlineText = 'INGYEN SZÁLLÍTÁS';
        stockChipText = 'Külső raktár';
      }
      // Out állapotban a pill szövege „Elfogyott"
      if (!v.available) stockChipText = 'Elfogyott';
      // 2026-08-28: BESZÁLLÍTÁS ALATT. A variáns CONTINUE készletpolitikával megrendelhető
      // (available === true), de a készlet 0 — ilyenkor a „Külső raktár" félrevezető lenne.
      // A liquid teszi ki a strip-re a data-ap-backorder jelzőt.
      const isBackorder = !!(stockCard && stockCard.getAttribute('data-ap-backorder') === 'true' && v.inventory_quantity <= 0);
      if (isBackorder) {
        stockKlass = 'ap-inventory--backorder';
        legacyText = 'Beszállítás alatt';
        headlineText = 'ELŐRENDELHETŐ – BESZÁLLÍTÁS ALATT';
        stockChipText = 'Beszállítás alatt';
      }
      // Csak akkor írjunk az elemekbe, ha a tartalom/osztály ténylegesen változott
      // — így nem villog a már helyesen renderelt SSR HTML.
      if (inventoryEl) {
        if (!inventoryEl.classList.contains(stockKlass)) {
          inventoryEl.classList.remove('ap-inventory--in', 'ap-inventory--low', 'ap-inventory--out', 'ap-inventory--backorder');
          inventoryEl.classList.add(stockKlass);
        }
        if (inventoryEl.textContent !== legacyText) inventoryEl.textContent = legacyText;
      }
      if (stockCard && !stockCard.classList.contains(stockKlass)) {
        stockCard.classList.remove('ap-inventory--in', 'ap-inventory--low', 'ap-inventory--out', 'ap-inventory--backorder');
        stockCard.classList.add(stockKlass);
      }
      if (stockLabel && stockLabel.textContent !== headlineText) {
        stockLabel.textContent = headlineText;
      }
      if (stockTextEl && stockTextEl.textContent !== stockChipText) {
        stockTextEl.textContent = stockChipText;
      }

      // Mobil sticky ár
      if (stickyPriceStrong) {
        stickyPriceStrong.textContent = formatMoney(v.price, config.currency);
      }

      // ATC gomb állapot
      if (atcBtn) {
        atcBtn.disabled = !v.available;
        if (atcLabel) {
          atcLabel.textContent = v.available
            ? (t.add_to_cart || 'Kosárba')
            : (t.out_of_stock || 'Elfogyott');
        }
      }

      // Galéria — variant kép váltása
      if (v.featured_image_idx != null && root.galleryRef) {
        try {
          root.galleryRef.slideTo(v.featured_image_idx);
        } catch (e) {}
      }

      // URL paraméter
      if (!opts.skipUrl) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(id));
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}
      }

      // #selected-variant-id div szinkron — a popup MutationObserverekhez,
      // és a Cofidis/delivery initCofidisAndDelivery is ezt figyeli
      if (selectedDiv) {
        selectedDiv.setAttribute('data-variant-id', String(id));
        selectedDiv.setAttribute('data-variant-title', v.title || '');
      }

      // Kompat-shim radio inputok — a popup getSelectedProductVersion() ezt olvassa
      root.querySelectorAll('[data-ap-shim-radio]').forEach(r => {
        r.checked = (parseInt(r.value, 10) === id);
      });

      // Custom event
      root.dispatchEvent(new CustomEvent('ap:variant-change', { detail: v, bubbles: true }));
    }

    cards.forEach(card => {
      card.addEventListener('click', () => {
        if (card.classList.contains('is-out')) return;
        const id = parseInt(card.getAttribute('data-variant-id'), 10);
        if (id) selectVariant(id);
      });
    });

    // Szegmentált toggle-ök — 1 toggle (2-opciós termék) VAGY 2 toggle (3-opciós).
    // A kártya-csoport kulcsa az aktív toggle-értékek '~~'-vel összefűzve:
    //   2-opciós → "option1"   |   3-opciós → "option1~~option2"
    // (a Liquid ugyanezt a kulcsot rakja a data-ap-option-group attribútumba)
    const toggles = Array.from(root.querySelectorAll('[data-ap-option-toggle]'));
    if (toggles.length) {
      const groups   = root.querySelectorAll('[data-ap-option-group]');
      const optCount = toggles.length + 1;                 // 2 vagy 3
      const lastAttr = 'data-variant-option' + optCount;    // a tier-opció attribútuma

      function groupKey() {
        return toggles.map(function (tg) {
          const a = tg.querySelector('[data-ap-option-value].is-active');
          return a ? a.getAttribute('data-ap-option-value') : '';
        }).join('~~');
      }
      function showGroup() {
        const key = groupKey();
        groups.forEach(function (g) {
          const m = g.getAttribute('data-ap-option-group') === key;
          g.classList.toggle('is-active', m);
          g.hidden = !m;
        });
      }
      // Indulás: a Liquid által kirakott is-active toggle-ökből
      showGroup();

      toggles.forEach(function (tg) {
        tg.querySelectorAll('[data-ap-option-value]').forEach(function (seg) {
          seg.addEventListener('click', function () {
            // Aktív seg beállítása EZEN a toggle-ön belül
            tg.querySelectorAll('[data-ap-option-value]').forEach(function (s) {
              const on = s === seg;
              s.classList.toggle('is-active', on);
              s.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            showGroup();

            // Auto-select: tartsuk a jelenlegi tier-t (utolsó opció), az új toggle-kombóval
            const sel  = root.querySelector('[data-ap-variant-card].is-selected');
            const last = sel ? sel.getAttribute(lastAttr) : null;
            const gSel = '[data-ap-option-group="' + cssEscape(groupKey()) + '"]';
            let cand = null;
            if (last) {
              cand = root.querySelector(gSel + ' [data-ap-variant-card][' + lastAttr + '="' + cssEscape(last) + '"]');
            }
            if (!cand) {
              const gc = root.querySelectorAll(gSel + ' [data-ap-variant-card]:not(.is-out)');
              if (gc.length) cand = gc[0];
            }
            if (cand) {
              const id = parseInt(cand.getAttribute('data-variant-id'), 10);
              if (id) selectVariant(id);
            }
          });
        });
      });
    }

    // Globalban érhetővé tesszük az URL pre-select-et a galéria-init után
    root._apSelectVariant = selectVariant;
  }

  /* ---------------------------------------------------------------------
     DEAL PANEL — TopApp akció-szalag az ár felett (2026-07-23 redesign).
     Adatforrás: <script type="application/json" data-ap-sale-json> — a
     topapp.sale metafield (name, ends_at, variants[], prices{}, lowest_30d{}).
     Feladatok:
       · élő visszaszámláló a szalagban (SSR-elt kezdőértékekre ír rá),
       · variánsváltáskor (ap:variant-change) eldönti, hogy az aktuális
         variáns akciós-e → szalag-név / timer / omnibus frissítés,
       · lejáratkor a timert elrejti, a név "Akciós ár"-ra vált (az árakat
         a Shopify compare_at kezeli, azokhoz nem nyúl).
     Megjegyzés: a lowest_30d értékek FORINTBAN jönnek (nem fillérben),
     ezért itt saját formázó van, nem a formatMoney.
     --------------------------------------------------------------------- */
  function initDealPanel(root) {
    const deal = root.querySelector('[data-ap-deal]');
    if (!deal) return;

    const jsonEl = root.querySelector('[data-ap-sale-json]');
    let sale = null;
    if (jsonEl) {
      try { sale = JSON.parse(jsonEl.textContent || 'null'); } catch (e) { sale = null; }
    }

    const nameEl = deal.querySelector('[data-ap-deal-name]');
    const timerEl = deal.querySelector('[data-ap-deal-timer]');
    const omniEl = deal.querySelector('[data-ap-deal-omnibus]');
    const dEl = deal.querySelector('[data-ap-deal-d]');
    const hEl = deal.querySelector('[data-ap-deal-h]');
    const mEl = deal.querySelector('[data-ap-deal-m]');
    const sEl = deal.querySelector('[data-ap-deal-s]');

    const endsAt = sale && sale.ends_at ? new Date(sale.ends_at).getTime() : 0;
    const ids = (sale && sale.variants ? sale.variants : []).map(String);
    const lowest = (sale && sale.lowest_30d) || null;

    function fmtFt(v) {
      const n = Math.round(Number(v) || 0);
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' Ft';
    }
    function pad(n) { return n < 10 ? '0' + n : String(n); }
    function currentId() {
      const inp = root.querySelector('[data-ap-variant-id]');
      return inp && inp.value ? String(inp.value) : '';
    }
    function saleActiveForCurrent() {
      return !!(sale && endsAt && endsAt > Date.now() && ids.indexOf(currentId()) !== -1);
    }

    function tick() {
      if (!endsAt || !dEl) return;
      const left = endsAt - Date.now();
      if (left <= 0) {
        // Lejárt — timer el, név vissza az alapra. (Az árakat a TopApp
        // worker állítja vissza; itt csak a szalagot normalizáljuk.)
        if (timerEl) timerEl.hidden = true;
        if (nameEl) nameEl.textContent = 'Akciós ár';
        clearInterval(iv);
        return;
      }
      const secs = Math.floor(left / 1000);
      dEl.textContent = String(Math.floor(secs / 86400));
      if (hEl) hEl.textContent = pad(Math.floor((secs % 86400) / 3600));
      if (mEl) mEl.textContent = pad(Math.floor((secs % 3600) / 60));
      if (sEl) sEl.textContent = pad(secs % 60);
    }

    function update() {
      const active = saleActiveForCurrent();
      if (nameEl) {
        const label = active && sale.name ? sale.name : 'Akciós ár';
        if (nameEl.textContent !== label) nameEl.textContent = label;
      }
      if (timerEl) timerEl.hidden = !active;
      if (omniEl) {
        const vid = currentId();
        // review-fix: csak akkor, ha az ár TÉNYLEG csökkentett (is-sale) —
        // kint maradt metafield ne mutasson omnibus-sort teljes áras variánson.
        const priceIsSale = deal.classList.contains('is-sale');
        if (active && priceIsSale && lowest && lowest[vid] != null) {
          omniEl.textContent = 'Az árcsökkentést megelőző 30 nap legalacsonyabb ára: ' + fmtFt(lowest[vid]) + '.';
          omniEl.hidden = false;
        } else {
          omniEl.hidden = true;
        }
      }
      if (active) tick();
    }

    let iv = null;
    if (endsAt && endsAt > Date.now()) {
      iv = setInterval(function () {
        if (timerEl && !timerEl.hidden) tick();
      }, 1000);
    }

    root.addEventListener('ap:variant-change', update);
    window.addEventListener('popstate', update);
    update();
  }

  // Kis CSS.escape polyfill az attribute selector-okhoz
  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, c => '\\' + c);
  }

  function preselectVariantFromUrl(root, config) {
    if (!root._apSelectVariant) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const vid = parseInt(params.get('variant'), 10);
      if (vid) root._apSelectVariant(vid, { skipUrl: true });
    } catch (e) {}
  }

  /* ---------------------------------------------------------------------
     COFIDIS Express ecommerce iframe + DELIVERY TIME — variant-szinkron.

     FONTOS: A hivatalos cetelem-ecommerce.js script `document.write`-ot
     használ, ami page-load után kifehéríti a teljes oldalt. Ezért NEM
     a scriptet használjuk, hanem közvetlenül építjük az iframe-et a
     Cofidis Calc.action URL-jével — ez a támogatott embed-pattern és
     pontosan ugyanazt a kalkulátor UI-t mutatja.
     --------------------------------------------------------------------- */
  const COFIDIS_CALC_URL = 'https://ecommercedemo.cofidisbank.hu/ecommerce/Calc.action';

  function buildCofidisUrl(cfg, purchaseAmount) {
    const params = new URLSearchParams();
    params.set('shopCode', String(cfg.shopCode));
    params.set('barem', String(cfg.barem));
    params.set('purchaseAmount', String(purchaseAmount));
    params.set('variant', cfg.variant);
    return COFIDIS_CALC_URL + '?' + params.toString();
  }

  function initCofidisAndDelivery(root, config) {
    const cofidis = root.querySelector('[data-ap-cofidis]');
    const detailBtn = root.querySelector('[data-ap-cofidis-info]');
    const detail = root.querySelector('[data-ap-cofidis-detail]');
    const iframeTarget = root.querySelector('[data-ap-cofidis-iframe]');
    const loadingEl = root.querySelector('[data-ap-cofidis-loading]');
    const deliveryWrap = root.querySelector('[data-ap-delivery]');
    const deliveryText = root.querySelector('[data-ap-delivery-text]');
    const variants = (config.variants) || [];
    const byId = {};
    variants.forEach(v => { byId[v.id] = v; });

    let currentPriceHuf = 0;
    let mounted = false;
    let detailEverOpened = false;

    function getCofidisCfg() {
      if (!cofidis) return null;
      const shopCode = parseInt(cofidis.getAttribute('data-cofidis-shop-code'), 10);
      const barem = parseInt(cofidis.getAttribute('data-cofidis-barem'), 10);
      const width = parseInt(cofidis.getAttribute('data-cofidis-width'), 10) || 340;
      const height = parseInt(cofidis.getAttribute('data-cofidis-height'), 10) || 280;
      const variant = cofidis.getAttribute('data-cofidis-variant') || 'fekvo';
      const minPrice = parseInt(cofidis.getAttribute('data-cofidis-min-price'), 10) || 25000;
      const maxPrice = parseInt(cofidis.getAttribute('data-cofidis-max-price'), 10) || 2000000;
      if (!shopCode || !barem) return null;
      return { shopCode, barem, width, height, variant, minPrice, maxPrice };
    }

    function isInRange(priceHuf, cfg) {
      return priceHuf >= cfg.minPrice && priceHuf <= cfg.maxPrice;
    }

    function setLoading(visible) {
      if (!loadingEl) return;
      loadingEl.style.display = visible ? '' : 'none';
    }

    function clearIframe() {
      if (iframeTarget) iframeTarget.innerHTML = '';
      mounted = false;
    }

    function mountCofidisCalc() {
      if (!iframeTarget || !cofidis) return;
      const cfg = getCofidisCfg();
      if (!cfg) return;
      if (currentPriceHuf <= 0 || !isInRange(currentPriceHuf, cfg)) return;

      setLoading(true);
      clearIframe();

      const iframe = document.createElement('iframe');
      iframe.src = buildCofidisUrl(cfg, currentPriceHuf);
      iframe.width = String(cfg.width);
      iframe.height = String(cfg.height);
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('scrolling', 'no');
      iframe.setAttribute('title', 'Cofidis Express részletfizetés kalkulátor');
      iframe.style.border = '0';
      iframe.style.maxWidth = '100%';
      iframe.style.display = 'block';

      let settled = false;
      const onLoad = () => {
        if (settled) return;
        settled = true;
        setLoading(false);
      };
      const onError = () => {
        if (settled) return;
        settled = true;
        setLoading(false);
        if (iframeTarget) {
          iframeTarget.innerHTML = '<div class="ap-cofidis-iframe-error">A kalkulátor jelenleg nem elérhető. Kérjük, próbáld meg később.</div>';
        }
      };
      iframe.addEventListener('load', onLoad);
      iframe.addEventListener('error', onError);
      // Biztonsági timeout: ha 8s alatt nem szól vissza, levesszük a loadert
      setTimeout(onLoad, 8000);

      iframeTarget.appendChild(iframe);
      mounted = true;
    }

    if (detailBtn && detail) {
      detailBtn.addEventListener('click', (e) => {
        // Form-on belüli kontextus esetén védőháló: ne submitelje a kosár formot
        e.preventDefault();
        e.stopPropagation();
        detail.hidden = !detail.hidden;
        const isOpen = !detail.hidden;
        detailBtn.classList.toggle('is-open', isOpen);
        detailBtn.setAttribute('aria-expanded', String(isOpen));
        if (isOpen) {
          detailEverOpened = true;
          if (!mounted) mountCofidisCalc();
        }
      });
    }

    function updateForVariant(v) {
      if (!v) return;

      if (cofidis) {
        const cfg = getCofidisCfg();
        const priceHuf = Math.round((v.price || 0) / 100);
        const priceChanged = priceHuf !== currentPriceHuf;
        currentPriceHuf = priceHuf;

        if (cfg) {
          const shouldShow = isInRange(priceHuf, cfg);
          const isShown = cofidis.style.display !== 'none';
          if (shouldShow !== isShown) {
            cofidis.style.display = shouldShow ? '' : 'none';
          }
          if (!shouldShow) {
            if (mounted) clearIframe();
          } else if (priceChanged && (mounted || detailEverOpened)) {
            mountCofidisCalc();
          }
        }
      }

      if (deliveryWrap && deliveryText) {
        deliveryWrap.classList.remove('ap-delivery--out', 'ap-delivery--slow', 'ap-delivery--fast');
        // 2026-08-28: beszállítás alatt → megrendelhető, de nincs raktáron
        const backorderCard = root.querySelector('[data-ap-info-stock][data-ap-backorder="true"]');
        if (backorderCard && v.inventory_quantity <= 0) {
          deliveryWrap.classList.add('ap-delivery--slow');
          deliveryText.textContent = 'most megrendelhető – beérkezés után azonnal postázzuk';
        } else if (!v.available) {
          deliveryWrap.classList.add('ap-delivery--out');
          deliveryText.textContent = '— nézd meg a hasonló termékeket';
        } else if (v.inventory_quantity > 0) {
          deliveryWrap.classList.add('ap-delivery--fast');
          deliveryText.textContent = '1–3 munkanap';
        } else {
          deliveryWrap.classList.add('ap-delivery--slow');
          deliveryText.textContent = '5–12 munkanap';
        }
      }
    }

    root.addEventListener('ap:variant-change', e => updateForVariant(e.detail));

    const sel = document.getElementById('selected-variant-id');
    if (sel) {
      const observer = new MutationObserver(() => {
        const id = parseInt(sel.getAttribute('data-variant-id'), 10);
        if (id && byId[id]) updateForVariant(byId[id]);
      });
      observer.observe(sel, { attributes: true, attributeFilter: ['data-variant-id', 'data-variant-title'] });
    }

    const initialId = sel ? parseInt(sel.getAttribute('data-variant-id'), 10) : null;
    if (initialId && byId[initialId]) {
      updateForVariant(byId[initialId]);
    } else if (cofidis) {
      const fallbackPrice = parseInt(cofidis.getAttribute('data-purchase-amount'), 10) || 0;
      currentPriceHuf = fallbackPrice;
    }
  }

  function waitForSwiper(cb, attempts) {
    attempts = attempts || 0;
    if (typeof window.Swiper !== 'undefined') {
      cb();
      return;
    }
    if (attempts > 60) return; // ~6s max
    setTimeout(function () { waitForSwiper(cb, attempts + 1); }, 100);
  }

  /* ---------------------------------------------------------------------
     IMAGE GALLERY (Swiper)
     --------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------
     IMAGE GALLERY v2 — Native CSS scroll-snap (Swiper-mentes)
     - Natív browser swipe → tökéletes mobilos UX, race condition-mentes
     - WebP support + responsive srcset
     - Resize-on automatikusan újraszámítja magát (CSS feladata)
     - Minimális JS: csak prev/next buttons, dot/thumb sync, variant-image
     --------------------------------------------------------------------- */
  function initNativeGallery(root) {
    const mg = root.querySelector('[data-ap-mg]');
    if (!mg) return;
    const track = mg.querySelector('[data-ap-mg-track]');
    if (!track) return;

    const slides = Array.from(track.querySelectorAll('[data-ap-mg-slide]'));
    if (slides.length <= 1) return; // 1 kép, semmi navigáció kell

    const thumbs = root.querySelectorAll('[data-ap-thumb]');
    const thumbsWrap = root.querySelector('.ap-thumbs');
    // A dots már nem a .ap-mg-n belül, hanem külön bar a frame után — root-on keressük
    const dots = root.querySelectorAll('[data-ap-mg-dot]');
    const prevBtn = mg.querySelector('[data-ap-mg-prev]');
    const nextBtn = mg.querySelector('[data-ap-mg-next]');

    function activeIndex() {
      // A track scroll pozíciója alapján: melyik slide van a középen
      const trackCenter = track.scrollLeft + track.clientWidth / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const dist = Math.abs(slideCenter - trackCenter);
        if (dist < bestDist) { bestDist = dist; bestIdx = i; }
      });
      return bestIdx;
    }

    function scrollToIndex(idx, smooth) {
      idx = Math.max(0, Math.min(slides.length - 1, idx));
      const slide = slides[idx];
      if (!slide) return;
      track.scrollTo({
        left: slide.offsetLeft,
        behavior: smooth === false ? 'auto' : 'smooth'
      });
    }

    function scrollThumbIntoView(thumb) {
      if (!thumb || !thumbsWrap) return;
      const wrap = thumbsWrap.getBoundingClientRect();
      const t = thumb.getBoundingClientRect();
      if (t.left < wrap.left || t.right > wrap.right) {
        const offset = thumb.offsetLeft - thumbsWrap.offsetLeft - (thumbsWrap.clientWidth - thumb.clientWidth) / 2;
        try {
          thumbsWrap.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
        } catch (e) {
          thumbsWrap.scrollLeft = Math.max(0, offset);
        }
      }
    }

    function preloadNeighbors(idx) {
      // Az aktív kép körüli ±2 képet "eager"-re állítjuk — a böngésző elkezdi
      // letölteni őket, mire váltáskor szükség lesz rájuk.
      // FONTOS: decoding="async" maradjon (default), különben layout-thrash + flicker.
      slides.forEach((slide, i) => {
        const img = slide.querySelector('img');
        if (!img) return;
        if (Math.abs(i - idx) <= 2) {
          if (img.loading !== 'eager') img.loading = 'eager';
        }
      });
    }

    function syncUI(idx) {
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === idx));
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === idx);
        d.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
      if (thumbs[idx]) scrollThumbIntoView(thumbs[idx]);
      preloadNeighbors(idx);
    }

    // Track scroll → requestAnimationFrame-alapú UI sync (sokkal gyorsabb, mint a setTimeout)
    let scrollRaf = 0;
    let lastSyncedIdx = -1;
    track.addEventListener('scroll', () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        const idx = activeIndex();
        if (idx !== lastSyncedIdx) {
          lastSyncedIdx = idx;
          syncUI(idx);
        }
      });
    }, { passive: true });

    // Thumbnail kattintás → scroll
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.getAttribute('data-idx'), 10) || 0;
        scrollToIndex(idx, true);
        thumb.blur();
      });
    });

    // Dot kattintás
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.getAttribute('data-idx'), 10) || 0;
        scrollToIndex(idx, true);
      });
    });

    // Prev / Next gombok
    if (prevBtn) prevBtn.addEventListener('click', () => {
      const cur = activeIndex();
      // Rewind: ha az első előtt vagyunk, ugorjunk az utolsóra
      const target = cur <= 0 ? slides.length - 1 : cur - 1;
      scrollToIndex(target, true);
    });
    if (nextBtn) nextBtn.addEventListener('click', () => {
      const cur = activeIndex();
      // Rewind: ha az utolsón vagyunk, ugorjunk az elsőre
      const target = cur >= slides.length - 1 ? 0 : cur + 1;
      scrollToIndex(target, true);
    });

    // Variant kép váltáshoz exposed API
    root.galleryRef = {
      slideTo: (idx) => scrollToIndex(idx, true),
      slideToInstant: (idx) => scrollToIndex(idx, false),
      getCurrent: activeIndex,
    };

    // Kezdeti állapot — a current variant featured képét mutassuk
    let initialIdx = 0;
    try {
      const cfgScript = root.querySelector('[data-ap-config]');
      if (cfgScript) {
        const cfg = JSON.parse(cfgScript.textContent || '{}');
        const selectedCard = root.querySelector('[data-ap-variant-card].is-selected');
        if (selectedCard && cfg.variants) {
          const vid = parseInt(selectedCard.getAttribute('data-variant-id'), 10);
          const v = (cfg.variants || []).find(x => x.id === vid);
          if (v && typeof v.featured_image_idx === 'number'
              && v.featured_image_idx >= 0 && v.featured_image_idx < slides.length) {
            initialIdx = v.featured_image_idx;
          }
        }
      }
    } catch (e) {}
    if (initialIdx > 0) {
      // instant ugrás (animation nélkül) az induló képre
      requestAnimationFrame(() => scrollToIndex(initialIdx, false));
    }
    syncUI(initialIdx);

    // ─── Thumb-sor két oldali lapozó nyilai ───
    // FONTOS: ezek a nyilak CSAK a thumb-konténer scrollLeft-jét tolják el,
    // a fő képet NEM változtatják. Ha a fő kép másik képre vált
    // (variant-kapcsoláson keresztül vagy thumb-clickkel), a `syncUI` már
    // hívja a `scrollThumbIntoView`-t, ami visszaállítja a thumb-sor pozícióját
    // úgy hogy az aktív thumb látható legyen.
    const thumbsArrowWrap = root.querySelector('[data-ap-thumbs-wrap]');
    const thumbsPrev = thumbsArrowWrap && thumbsArrowWrap.querySelector('[data-ap-thumbs-prev]');
    const thumbsNext = thumbsArrowWrap && thumbsArrowWrap.querySelector('[data-ap-thumbs-next]');
    if (thumbsWrap && (thumbsPrev || thumbsNext)) {
      function getThumbStep() {
        const firstThumb = thumbsWrap.querySelector('.ap-thumb');
        if (!firstThumb) return 96;
        const style = window.getComputedStyle(thumbsWrap);
        const gap = parseFloat(style.columnGap || style.gap || '10') || 10;
        return firstThumb.offsetWidth + gap;
      }
      function updateArrowDisabled() {
        const max = thumbsWrap.scrollWidth - thumbsWrap.clientWidth;
        const left = thumbsWrap.scrollLeft;
        // 2px tolerance — sub-pixel scroll-pozíciókra
        if (thumbsPrev) thumbsPrev.classList.toggle('is-hidden', left <= 2);
        if (thumbsNext) thumbsNext.classList.toggle('is-hidden', left >= max - 2);
        // Ha nincs túlcsordulás (minden thumb látható), mindkét nyilat elrejtjük
        if (max <= 2) {
          if (thumbsPrev) thumbsPrev.classList.add('is-hidden');
          if (thumbsNext) thumbsNext.classList.add('is-hidden');
        }
      }
      if (thumbsPrev) thumbsPrev.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        thumbsWrap.scrollBy({ left: -getThumbStep(), behavior: 'smooth' });
      });
      if (thumbsNext) thumbsNext.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        thumbsWrap.scrollBy({ left: getThumbStep(), behavior: 'smooth' });
      });
      thumbsWrap.addEventListener('scroll', updateArrowDisabled, { passive: true });
      window.addEventListener('resize', updateArrowDisabled, { passive: true });
      // Várjunk a layout-ra (a thumb-ok offsetWidth-je csak ekkor pontos)
      requestAnimationFrame(updateArrowDisabled);
    }
  }

  /* ---------------------------------------------------------------------
     MAIN IMAGE ZOOM — kattintható nagyítás a fő képre (mobil + desktop)
     - Önálló lightbox-szerű modal, lazy-build az első klikknél
     - Prev / Next gombok, ESC + click-outside close, mobil swipe
     - Klikk a képen → 2x zoom focal pointtal
     --------------------------------------------------------------------- */
  function initMainImageZoom(root) {
    const slides = Array.from(root.querySelectorAll('[data-ap-mg-slide]'));
    if (!slides.length) return;

    // Gyűjtjük a slide-okból a nagy URL-eket és captioneket
    function collectItems() {
      const out = [];
      slides.forEach((slide, i) => {
        const img = slide.querySelector('img');
        if (!img) return;
        // A legnagyobb forrás: srcset utolsó (legnagyobb width), vagy src
        const srcset = img.getAttribute('srcset') || '';
        let bigSrc = img.currentSrc || img.src;
        if (srcset) {
          // Parse: "url 600w, url 900w, url 1200w, url 1600w" — vegyük a legnagyobbat
          const candidates = srcset.split(',').map(s => {
            const parts = s.trim().split(/\s+/);
            const url = parts[0];
            const w = parseInt(parts[1], 10) || 0;
            return { url, w };
          }).filter(x => x.url);
          if (candidates.length) {
            candidates.sort((a, b) => b.w - a.w);
            bigSrc = candidates[0].url;
          }
        }
        const alt = img.getAttribute('alt') || '';
        out.push({ src: bigSrc, cap: alt, slideIdx: i });
      });
      return out;
    }

    // Lazy build a modal markup-ot
    let lb = null, lbImg = null, lbCap = null, lbCounter = null;
    let items = [];
    let cur = 0;

    function buildLightbox() {
      if (lb) return;
      lb = document.createElement('div');
      lb.className = 'ap-zoom';
      lb.setAttribute('role', 'dialog');
      lb.setAttribute('aria-modal', 'true');
      lb.setAttribute('aria-label', 'Kép nagyítás');
      lb.innerHTML = `
        <button type="button" class="ap-zoom-btn ap-zoom-close" aria-label="Bezárás">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <span class="ap-zoom-counter"></span>
        <button type="button" class="ap-zoom-btn ap-zoom-prev" aria-label="Előző">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div class="ap-zoom-stage">
          <img class="ap-zoom-img" alt="">
          <span class="ap-zoom-cap"></span>
        </div>
        <button type="button" class="ap-zoom-btn ap-zoom-next" aria-label="Következő">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      `;
      document.body.appendChild(lb);
      lbImg     = lb.querySelector('.ap-zoom-img');
      lbCap     = lb.querySelector('.ap-zoom-cap');
      lbCounter = lb.querySelector('.ap-zoom-counter');

      lb.querySelector('.ap-zoom-close').addEventListener('click', close);
      lb.querySelector('.ap-zoom-prev').addEventListener('click', () => go(-1));
      lb.querySelector('.ap-zoom-next').addEventListener('click', () => go(1));
      lb.addEventListener('click', e => { if (e.target === lb) close(); });
      lbImg.addEventListener('click', toggleZoom);

      let touchX = 0;
      lb.addEventListener('touchstart', e => { touchX = e.changedTouches[0].clientX; }, { passive: true });
      lb.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - touchX;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
      }, { passive: true });

      document.addEventListener('keydown', e => {
        if (!lb.classList.contains('is-open')) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') go(-1);
        else if (e.key === 'ArrowRight') go(1);
      });
    }

    function show() {
      const it = items[cur]; if (!it) return;
      lbImg.classList.remove('is-zoomed');
      lbImg.style.transform = '';
      lbImg.src = it.src;
      lbImg.alt = it.cap || '';
      lbCap.textContent = it.cap || '';
      lbCounter.textContent = `${cur + 1} / ${items.length}`;
      const showNav = items.length > 1;
      lb.querySelector('.ap-zoom-prev').style.visibility = showNav ? 'visible' : 'hidden';
      lb.querySelector('.ap-zoom-next').style.visibility = showNav ? 'visible' : 'hidden';
    }

    function openAt(idx) {
      buildLightbox();
      items = collectItems();
      if (!items.length) return;
      // Mapping: a kattintott slide-indexet a megfelelő items indexre konvertáljuk
      cur = items.findIndex(it => it.slideIdx === idx);
      if (cur < 0) cur = 0;
      document.body.style.overflow = 'hidden';
      lb.classList.add('is-open');
      show();
    }

    function close() {
      lb.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lbImg) lbImg.src = '';
    }

    function go(delta) {
      if (!items.length) return;
      cur = (cur + delta + items.length) % items.length;
      show();
    }

    function toggleZoom(e) {
      e.stopPropagation();
      const zoomed = lbImg.classList.toggle('is-zoomed');
      if (zoomed) {
        const rect = lbImg.getBoundingClientRect();
        const fx = ((e.clientX - rect.left) / rect.width) * 100;
        const fy = ((e.clientY - rect.top) / rect.height) * 100;
        lbImg.style.transformOrigin = `${fx}% ${fy}%`;
        lbImg.style.transform = 'scale(2)';
      } else {
        lbImg.style.transform = '';
      }
    }

    // Bind kattintást a slide-okra (csak a képet zoomoljuk, videót nem)
    slides.forEach((slide, idx) => {
      const img = slide.querySelector('img');
      if (!img) return;
      slide.style.cursor = 'zoom-in';
      slide.addEventListener('click', e => {
        // Ne triggereljük navigációra ha pl. linkre kattintottunk
        if (e.target.closest('a, button')) return;
        e.preventDefault();
        openAt(idx);
      });
    });
  }

  // Legacy stub (Swiper-alapú) — most már nem fut, megmarad a régi hívásokhoz
  function initGallery(root) {
    const galleryEl = root.querySelector('[data-ap-gallery]');
    if (!galleryEl || typeof window.Swiper === 'undefined') return;

    const main = galleryEl.querySelector('.swiper');
    if (!main) return;

    // Több mint 1 slide esetén kapcsoljunk be a body-ra a wrapping logika.
    // Loop helyett rewind: ennek nincsenek clone-slidejei, így resize-kor
    // nem ugrik a gallery (Swiper 11 ismert loop+resize bug).
    const slideCount = main.querySelectorAll('.swiper-slide').length;
    const enableRewind = slideCount > 1;

    // Az induló slide a jelenlegi variánt featured képe (vagy 0 ha nincs)
    let initialIdx = 0;
    try {
      const cfgScript = root.querySelector('[data-ap-config]');
      if (cfgScript) {
        const cfg = JSON.parse(cfgScript.textContent || '{}');
        const selectedCard = root.querySelector('[data-ap-variant-card].is-selected');
        if (selectedCard && cfg.variants) {
          const vid = parseInt(selectedCard.getAttribute('data-variant-id'), 10);
          const v = (cfg.variants || []).find(x => x.id === vid);
          if (v && typeof v.featured_image_idx === 'number'
              && v.featured_image_idx >= 0 && v.featured_image_idx < slideCount) {
            initialIdx = v.featured_image_idx;
          }
        }
      }
    } catch (e) {}

    const swiper = new window.Swiper(main, {
      slidesPerView: 1,
      spaceBetween: 0,
      initialSlide: initialIdx,
      // Rewind: végén Next-re visszamegy az elsőre (loop helyett — nincs clone)
      rewind: enableRewind,
      loop: false,
      grabCursor: true,
      watchOverflow: true,
      // autoHeight: a Swiper az aktív slide tényleges magasságához igazodik.
      // Ez kiváltja a CSS aspect-ratio körkörös függését — viewport-váltáskor
      // a swiper saját maga méretezi újra a slide-okat (nem kell külön
      // ResizeObserver vagy window resize listener)
      autoHeight: true,
      navigation: {
        nextEl: galleryEl.querySelector('.swiper-button-next'),
        prevEl: galleryEl.querySelector('.swiper-button-prev'),
      },
      pagination: {
        el: galleryEl.querySelector('.swiper-pagination'),
        clickable: true,
      },
    });

    const thumbs = root.querySelectorAll('[data-ap-thumb]');
    const thumbsWrap = root.querySelector('.ap-thumbs');

    function scrollActiveThumbIntoView(thumb) {
      if (!thumb || !thumbsWrap) return;
      const wrap = thumbsWrap.getBoundingClientRect();
      const t = thumb.getBoundingClientRect();
      if (t.left < wrap.left || t.right > wrap.right) {
        const offset = thumb.offsetLeft - thumbsWrap.offsetLeft - (thumbsWrap.clientWidth - thumb.clientWidth) / 2;
        try {
          thumbsWrap.scrollTo({ left: Math.max(0, offset), behavior: 'smooth' });
        } catch (e) {
          thumbsWrap.scrollLeft = Math.max(0, offset);
        }
      }
    }

    function syncThumbs(realIdx) {
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === realIdx));
      if (thumbs[realIdx]) scrollActiveThumbIntoView(thumbs[realIdx]);
    }

    // Kezdeti szinkronizáció — a Swiper realIndex-e a forrás (loop esetén is helyes)
    syncThumbs(swiper.realIndex != null ? swiper.realIndex : initialIdx);

    // Thumbnail-kattintás → Swiper-en navigálunk
    thumbs.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => {
        swiper.slideTo(idx);
        thumb.blur();
      });
    });

    swiper.on('slideChange', () => {
      const real = swiper.realIndex != null ? swiper.realIndex : swiper.activeIndex;
      syncThumbs(real);
    });

    root.galleryRef = swiper;
  }

  /* ---------------------------------------------------------------------
     QUANTITY
     --------------------------------------------------------------------- */
  function initQuantity(root) {
    const minusBtn = root.querySelector('[data-ap-qty-minus]');
    const plusBtn = root.querySelector('[data-ap-qty-plus]');
    const input = root.querySelector('[data-ap-qty-input]');
    if (!input) return;

    function getVal() { return Math.max(1, parseInt(input.value, 10) || 1); }
    function setVal(v) { input.value = Math.max(1, v); }

    if (minusBtn) minusBtn.addEventListener('click', () => setVal(getVal() - 1));
    if (plusBtn) plusBtn.addEventListener('click', () => setVal(getVal() + 1));
    input.addEventListener('change', () => setVal(getVal()));
  }

  /* ---------------------------------------------------------------------
     VARIANT CARDS (Pro Selector)
     --------------------------------------------------------------------- */
  function initVariantCards(root, config) {
    const cards = root.querySelectorAll('[data-ap-variant-card]');
    if (!cards.length) return;

    const variantInput = root.querySelector('[data-ap-variant-id]');
    const priceEl = root.querySelector('[data-ap-price]');
    const compareEl = root.querySelector('[data-ap-price-compare]');
    const discountEl = root.querySelector('[data-ap-price-discount]');
    const inventoryEl = root.querySelector('[data-ap-inventory]');
    const atcBtn = root.querySelector('[data-ap-atc]');
    const stickyPrice = root.querySelector('[data-ap-sticky-price]');

    const variants = (config.variants) || [];
    const byId = {};
    variants.forEach(v => { byId[v.id] = v; });

    function selectCard(card) {
      const id = parseInt(card.getAttribute('data-variant-id'), 10);
      const v = byId[id];
      if (!v) return;

      cards.forEach(c => c.classList.remove('is-active'));
      card.classList.add('is-active');

      if (variantInput) variantInput.value = id;

      if (priceEl) priceEl.textContent = formatMoney(v.price, config.currency);
      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.textContent = formatMoney(v.compare_at_price, config.currency);
          compareEl.style.display = '';
          if (discountEl) {
            const pct = Math.round((1 - v.price / v.compare_at_price) * 100);
            discountEl.textContent = '-' + pct + '%';
            discountEl.style.display = '';
          }
        } else {
          compareEl.style.display = 'none';
          if (discountEl) discountEl.style.display = 'none';
        }
      }

      if (inventoryEl) {
        inventoryEl.classList.remove('ap-inventory--in', 'ap-inventory--low', 'ap-inventory--out');
        if (!v.available) {
          inventoryEl.classList.add('ap-inventory--out');
          inventoryEl.textContent = config.t.out_of_stock || 'Elfogyott';
        } else if (v.inventory_quantity > 0 && v.inventory_quantity < 5) {
          inventoryEl.classList.add('ap-inventory--low');
          inventoryEl.textContent = (config.t.low_stock_prefix || 'Csak') + ' ' + v.inventory_quantity + ' ' + (config.t.low_stock_suffix || 'db raktáron');
        } else {
          inventoryEl.classList.add('ap-inventory--in');
          inventoryEl.textContent = config.t.in_stock || 'Raktáron';
        }
      }

      if (atcBtn) {
        atcBtn.disabled = !v.available;
        atcBtn.textContent = v.available
          ? (config.t.add_to_cart || 'Kosárba')
          : (config.t.out_of_stock || 'Elfogyott');
      }

      if (stickyPrice) {
        const strong = stickyPrice.querySelector('strong');
        if (strong) strong.textContent = formatMoney(v.price, config.currency);
      }

      // URL state — Shopify history
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(id));
        window.history.replaceState({}, '', url.toString());
      } catch (e) { /* ignore */ }

      // Featured image swap
      if (v.featured_image_idx != null && root.galleryRef) {
        root.galleryRef.slideTo(v.featured_image_idx);
      }

      // Notify other listeners (theme.js may listen for this)
      root.dispatchEvent(new CustomEvent('ap:variant-change', { detail: v, bubbles: true }));
    }

    cards.forEach(card => {
      card.addEventListener('click', () => {
        if (card.classList.contains('is-disabled')) return;
        selectCard(card);
      });
    });

    // Pre-select if URL has variant
    try {
      const params = new URLSearchParams(window.location.search);
      const vid = params.get('variant');
      if (vid) {
        const card = root.querySelector('[data-variant-id="' + vid + '"]');
        if (card) selectCard(card);
      }
    } catch (e) { /* ignore */ }
  }

  /* ---------------------------------------------------------------------
     ACCORDION
     --------------------------------------------------------------------- */
  function initAccordion(root) {
    // Native <details> works fine — only need to handle "one open at a time" if desired
    // We allow multiple open by default for better UX
  }

  /* ---------------------------------------------------------------------
     STICKY PRICE BAR
     --------------------------------------------------------------------- */
  /* ---------------------------------------------------------------------
     GIFT ACTIVATION — Mikrofon + Antenna erősítő
     - Kattintásra aktiválja az ajándékot (localStorage per termék)
     - ATC kattintás után a 2 ajándék variant a kosárba kerül
       line-property-vel: `Ajándék: Igen` és `_gift_parent: <handle>`
     - A „csak a termékkel együtt érvényes" enforcement Shopify Admin
       oldalon Buy X Get Y / automatic discount-tal állítható be
     --------------------------------------------------------------------- */
  function initGiftActivation(root) {
    const gift = root.querySelector('[data-ap-gift]');
    if (!gift) return;

    const productHandle = gift.getAttribute('data-product-handle') || '';
    const micId = parseInt(gift.getAttribute('data-mic-id'), 10);
    // antennaId opcionális — B variánsnál üres data-antenna-id ("") esetén NaN → csak mic
    const antennaRaw = gift.getAttribute('data-antenna-id') || '';
    const antennaId = antennaRaw ? parseInt(antennaRaw, 10) : 0;
    // Text-szövegek a Liquid-ben már variánsfüggően ki vannak töltve a data-attr-ekben.
    // Fallback (régi termékoldalakhoz): a HTML-ben jelenleg látható szöveg
    const textInactive = gift.getAttribute('data-text-inactive') || 'Külső mikrofon + Antenna erősítő';
    const textActive   = gift.getAttribute('data-text-active')   || (textInactive + ' — már a tied');
    const eyebrow = gift.querySelector('[data-ap-gift-eyebrow]');
    const text = gift.querySelector('[data-ap-gift-text]');
    const word = gift.querySelector('[data-ap-gift-word]');
    const ctaText = gift.querySelector('.ap-gift-cta-text');

    const storageKey = 'topnavi_gift_active_' + productHandle;
    const addedKey = 'topnavi_gift_added_' + productHandle;
    const globalInCartKey = 'topnavi_gift_in_cart'; // globális — több termékről sem tud duplán berakni
    const couponCode = gift.getAttribute('data-coupon-code') || '';
    const freemicVariant = gift.getAttribute('data-freemic-variant') || 'A';
    // Variánsonként külön session-flag, hogy A/B/C külön-külön egyszer apply-zódjon
    const discountKey = 'topnavi_freemic_applied_' + (freemicVariant || 'NONE');

    function applyState(active) {
      gift.classList.toggle('is-active', active);
      gift.setAttribute('aria-pressed', String(active));
      if (active) {
        if (eyebrow) eyebrow.textContent = '✓ Aktiválva — már a tied';
        if (word) word.textContent = 'Sikeresen aktiváltad!';
        if (text) text.textContent = textActive;
        if (ctaText) ctaText.textContent = 'Aktiválva';
      } else {
        if (eyebrow) eyebrow.textContent = 'Aktiváld az ajándékod';
        if (word) word.textContent = 'Aktiváld az ajándékod!';
        if (text) text.textContent = textInactive;
        if (ctaText) ctaText.textContent = 'Aktiválom';
      }
    }

    // Per-product gift check — csak EZ a termék gift-jeit veszi figyelembe
    // (_gift_parent property = productHandle). Más termék gift-jei nem zavarnak —
    // több main product egymástól függetlenül aktiválhatja a kupont.
    //
    // FONTOS: az ÖSSZES ismert gift-variant ID-t nézzük (mic + std antenna + VW antenna),
    // NEM csak a current variant-ét. Ez azért kell, mert ha az admin közben
    // átállította a freemic_variant metafieldet (pl. A→B), a régi std antenna
    // még a kosárban lehet ennek a parent-nek a _gift_parent property-vel — ne
    // adjunk hozzá újabb mic-et csak mert a current variant antennaId-ja már 0.
    // A cart-template.liquid cleanup-pass kitakarítja a stale gift-eket.
    var KNOWN_GIFT_IDS = [51064671338836, 51263915295060, 53761630437716];
    function cartHasGiftItemsForThisProduct(cart) {
      if (!cart || !Array.isArray(cart.items)) return false;
      return cart.items.some(function(item) {
        var vid = item.variant_id || item.id;
        if (KNOWN_GIFT_IDS.indexOf(vid) === -1) return false;
        var parent = item.properties &&
                     (item.properties._gift_parent || item.properties['_gift_parent']);
        return parent === productHandle;
      });
    }

    // Inicializáció: a state-et a SZERVER (Liquid) már beállította a HTML-ben
    // a `data-initial-active` attribútum + .is-active class alapján — `cart.items`
    // ground-truth-ról. Itt NEM piszkáljuk a state-et localStorage alapján, mert
    // az okozta a „bevillanó aktivált" flash-t: stale localStorage → instant
    // active → /cart.js empty → instant inactive flip = vizuális flicker.
    const serverActive = gift.getAttribute('data-initial-active') === 'true';

    // /cart.js per-product check — sanity sync csak akkor, ha a kosár tényleg
    // különbözik a szerver-rendered state-től (pl. user másik fülön módosította).
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        const inCart = cartHasGiftItemsForThisProduct(cart);
        if (inCart && !serverActive) {
          applyState(true);
          try { localStorage.setItem(storageKey, 'true'); } catch (e) {}
        } else if (!inCart && serverActive) {
          try { localStorage.removeItem(storageKey); } catch (e) {}
          try { sessionStorage.removeItem(addedKey); } catch (e) {}
          applyState(false);
        } else if (inCart) {
          try { localStorage.setItem(storageKey, 'true'); } catch (e) {}
        } else {
          try { localStorage.removeItem(storageKey); } catch (e) {}
          try { sessionStorage.removeItem(addedKey); } catch (e) {}
        }
      })
      .catch(() => {});

    // Kattintás: aktiválás + AZONNALI kosárba helyezés + FREEMIC apply
    gift.addEventListener('click', () => {
      if (gift.classList.contains('is-active')) return;
      applyState(true);
      try { localStorage.setItem(storageKey, 'true'); } catch (e) {}

      // Vizuális visszacsatolás
      gift.classList.add('is-pulse');
      setTimeout(() => gift.classList.remove('is-pulse'), 800);

      addGiftsToCart();
    });

    // Cart-add — per-product duplikáció-védelemmel.
    // Több main product mindegyikéhez külön mic (+ esetleg antenna) jár, ezért NINCS
    // globális blokk. A duplikációt a per-handle addedKey + per-product /cart.js
    // check együtt biztosítja.
    //
    // B variánsnál antennaId === 0 → CSAK mic kerül a kosárba (gift label is "Külső mikrofon").
    function addGiftsToCart() {
      if (!gift.classList.contains('is-active')) return;
      if (!micId) return; // mic minden variánsban kötelező

      // Per-handle session-duplikáció (ugyanezen termékhez ne adja kétszer)
      let alreadyAdded = false;
      try { alreadyAdded = sessionStorage.getItem(addedKey) === 'true'; } catch (e) {}
      if (alreadyAdded) return;

      // Per-product cart-check /cart.js-ből — friss adat erre a termékre
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(r => r.json())
        .then(cart => {
          if (cartHasGiftItemsForThisProduct(cart)) {
            // EZ a termék gift-jei már a kosárban — csak flag set, nincs add
            try { sessionStorage.setItem(addedKey, 'true'); } catch (e) {}
            return;
          }
          performAdd();
        })
        .catch(() => performAdd()); // /cart.js fail → próbáljuk az addot
    }

    function performAdd() {
      const items = [
        { id: micId, quantity: 1, properties: { 'Ajándék': 'Igen', '_gift_parent': productHandle } }
      ];
      // Antenna csak ha tényleg van (A=std antenna, C=VW antenna; B=nincs antenna)
      if (antennaId) {
        items.push({ id: antennaId, quantity: 1, properties: { 'Ajándék': 'Igen', '_gift_parent': productHandle } });
      }

      try { sessionStorage.setItem(addedKey, 'true'); } catch (e) {}

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      })
        .then(() => {
          // FREEMIC apply — variánsonként külön session-flag, és a Liquid-ből kapott
          // kuponkód (A/B/C). Ha üres a kód (defensive: B/C még nincs konfigolva),
          // skip — a cart-template.liquid amúgy is rendezi a discount-state-et.
          if (!couponCode) return;
          let alreadyApplied = false;
          try { alreadyApplied = sessionStorage.getItem(discountKey) === 'true'; } catch (e) {}
          if (alreadyApplied) return;
          try { sessionStorage.setItem(discountKey, 'true'); } catch (e) {}
          // A /discount/CODE URL a megbízható apply-módszer Shopify-ban (cookie set).
          fetch('/discount/' + encodeURIComponent(couponCode), { method: 'GET', credentials: 'same-origin' })
            .catch(err => {
              console.warn('FREEMIC apply failed (' + couponCode + '):', err);
              try { sessionStorage.removeItem(discountKey); } catch (e) {}
            });
        })
        .catch(err => {
          console.warn('Gift add failed:', err);
          try { sessionStorage.removeItem(addedKey); } catch (e) {}
        });
    }

    // ATC click safety net — ha valami miatt az activation click cart-add-je elmarad,
    // az ATC kattintásra is megpróbáljuk (a sessionStorage flag védi a duplikációtól)
    const atcBtn = root.querySelector('[data-ap-atc]');
    if (atcBtn) {
      atcBtn.addEventListener('click', () => {
        setTimeout(addGiftsToCart, 250);
      });
    }
  }

  function initStickyPrice(root) {
    const sticky = root.querySelector('[data-ap-sticky-bar]');
    if (!sticky) return;
    // Show after scrolling past the main ATC button.
    // 2026-07-12 (browser-review): scroll-listener + getBoundingClientRect →
    // IntersectionObserver. A gBCR minden scroll-eseményen forced layoutot
    // okozott a teljes termékoldal-élettartamra; az IO nulla per-scroll költség.
    const refBtn = root.querySelector('[data-ap-atc]');
    if (!refBtn) return;
    let visible = false;
    const io = new IntersectionObserver((entries) => {
      // A legfrissebb rekordra reagálunk — gyors görgetésnél az IO több
      // átlépést is egy batch-ben szállíthat, az első elavult lenne.
      const entry = entries[entries.length - 1];
      const shouldShow = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      if (shouldShow !== visible) {
        sticky.classList.toggle('is-visible', shouldShow);
        visible = shouldShow;
      }
    }, { threshold: 0 });
    io.observe(refBtn);
  }

  /* ---------------------------------------------------------------------
     MODAL — generic open/close (upgrade popup + version guide)
     --------------------------------------------------------------------- */
  function initModal(root) {
    const overlays = root.querySelectorAll('[data-ap-modal-overlay]');
    const triggers = root.querySelectorAll('[data-ap-modal-open]');
    const closes = root.querySelectorAll('[data-ap-modal-close]');

    function open(id) {
      const overlay = root.querySelector('[data-ap-modal-overlay="' + id + '"]');
      const modal = root.querySelector('[data-ap-modal="' + id + '"]');
      if (!overlay || !modal) return;
      overlay.classList.add('is-open');
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
    function close(id) {
      const overlay = id
        ? root.querySelector('[data-ap-modal-overlay="' + id + '"]')
        : null;
      const modal = id
        ? root.querySelector('[data-ap-modal="' + id + '"]')
        : null;
      if (overlay) overlay.classList.remove('is-open');
      if (modal) modal.classList.remove('is-open');
      // Restore body if no modal is open
      if (!root.querySelector('.ap-modal.is-open')) {
        document.body.style.overflow = '';
      }
    }

    triggers.forEach(t => {
      t.addEventListener('click', e => {
        e.preventDefault();
        const id = t.getAttribute('data-ap-modal-open');
        open(id);
      });
    });
    closes.forEach(c => {
      c.addEventListener('click', () => {
        const modal = c.closest('.ap-modal');
        const id = modal && modal.getAttribute('data-ap-modal');
        close(id);
      });
    });
    overlays.forEach(o => {
      o.addEventListener('click', () => {
        const id = o.getAttribute('data-ap-modal-overlay');
        close(id);
      });
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        const open = root.querySelector('.ap-modal.is-open');
        if (open) {
          const id = open.getAttribute('data-ap-modal');
          close(id);
        }
      }
    });

    // Expose for ATC upgrade flow
    root.modalAPI = { open: open, close: close };
  }

  /* ---------------------------------------------------------------------
     WARRANTY SELECTOR
     --------------------------------------------------------------------- */
  function initWarranty(root, config) {
    const cards = root.querySelectorAll('[data-ap-warranty-card]');
    if (!cards.length) return;
    const warrantyInput = root.querySelector('[data-ap-warranty-id]');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        const id = card.getAttribute('data-warranty-id') || '';
        if (warrantyInput) warrantyInput.value = id;
        recalcBundle(root);
      });
    });
  }

  /* ---------------------------------------------------------------------
     ACCESSORIES — Swiper init (deferred until Swiper is ready)
     --------------------------------------------------------------------- */
  function initAccessorySwiper(root) {
    const accSwiper = root.querySelector('[data-ap-accessories] .swiper');
    if (!accSwiper || typeof window.Swiper === 'undefined') return;
    new window.Swiper(accSwiper, {
      slidesPerView: 1.15,
      spaceBetween: 12,
      breakpoints: {
        640: { slidesPerView: 2.2 },
        900: { slidesPerView: 3.2 },
      },
      navigation: {
        nextEl: root.querySelector('[data-ap-accessories] .swiper-button-next'),
        prevEl: root.querySelector('[data-ap-accessories] .swiper-button-prev'),
      },
    });
  }

  /* ---------------------------------------------------------------------
     ACCESSORY TOGGLES — runs immediately, regardless of Swiper
     --------------------------------------------------------------------- */
  function initAccessoryToggles(root, config) {
    const RCA_ID = (config.accessories && config.accessories.rca_id) || null;
    const MIC_ID = (config.accessories && config.accessories.mic_id) || null;

    function isSelected(id) {
      const list = root._selectedAccessories || (root._selectedAccessories = []);
      return list.indexOf(id) >= 0;
    }
    function setSelected(id, on) {
      const list = root._selectedAccessories || (root._selectedAccessories = []);
      const i = list.indexOf(id);
      if (on && i < 0) list.push(id);
      if (!on && i >= 0) list.splice(i, 1);
    }
    function applyMicGate() {
      if (!MIC_ID) return;
      const micCard = root.querySelector('[data-accessory-id="' + MIC_ID + '"]');
      if (!micCard) return;
      const micToggle = micCard.querySelector('[data-ap-acc-toggle]');
      const rcaSelected = RCA_ID && isSelected(RCA_ID);
      if (rcaSelected) {
        micCard.classList.remove('is-disabled');
        if (micToggle) micToggle.disabled = false;
      } else {
        micCard.classList.add('is-disabled');
        if (micToggle) micToggle.disabled = true;
        // Force-deselect mic if was selected
        if (isSelected(MIC_ID)) {
          setSelected(MIC_ID, false);
          micCard.classList.remove('is-selected');
          if (micToggle) micToggle.textContent = config.t.add_btn || 'Hozzáadás';
        }
      }
    }

    root.querySelectorAll('[data-ap-acc-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const card = btn.closest('[data-accessory-id]');
        if (!card) return;
        const id = parseInt(card.getAttribute('data-accessory-id'), 10);
        if (!id || isNaN(id)) return;
        const on = !isSelected(id);
        setSelected(id, on);
        card.classList.toggle('is-selected', on);
        btn.textContent = on
          ? (config.t.added_btn || 'Hozzáadva')
          : (config.t.add_btn || 'Hozzáadás');
        applyMicGate();
        recalcBundle(root);
      });
    });

    applyMicGate();
  }

  /* ---------------------------------------------------------------------
     BUNDLE TOTAL recalc (running sum of selected accessories + warranty + variant price)
     --------------------------------------------------------------------- */
  function recalcBundle(root) {
    const totalEl = root.querySelector('[data-ap-bundle-total]');
    const countEl = root.querySelector('[data-ap-bundle-count]');
    if (!totalEl) return;
    let count = 0;
    let total = 0;
    root.querySelectorAll('[data-accessory-id].is-selected').forEach(c => {
      const p = parseInt(c.getAttribute('data-accessory-price'), 10) || 0;
      total += p;
      count += 1;
    });
    const warrantySel = root.querySelector('[data-ap-warranty-card].is-selected');
    if (warrantySel) {
      total += parseInt(warrantySel.getAttribute('data-warranty-price'), 10) || 0;
    }
    const cfgEl = root.querySelector('[data-ap-config]');
    let currency = 'HUF';
    if (cfgEl) {
      try { currency = (JSON.parse(cfgEl.textContent || '{}').currency) || currency; }
      catch (e) { /* ignore */ }
    }
    totalEl.textContent = formatMoney(total, currency);
    if (countEl) countEl.textContent = String(count);
  }
  function initBundleTotal(root) { recalcBundle(root); }

  /* ---------------------------------------------------------------------
     ATC → trigger upgrade popup for ALAP / STANDARD
     --------------------------------------------------------------------- */
  function bindAtcUpgrade(root, config) {
    const atcBtn = root.querySelector('[data-ap-atc]');
    if (!atcBtn) return;
    // Upgrade popup viselkedése tier szerint:
    //   - ALAP / STANDARD / PRO  → popup nyílik, csak az ACCESSORY upsell látszik (warranty rejtve)
    //   - ELITE / PREMIUM        → popup nyílik, WARRANTY + ACCESSORY látszik
    //   - egyéb tier             → nincs popup, natív cart-add

    atcBtn.addEventListener('click', e => {
      const card = root.querySelector('[data-ap-variant-card].is-active');
      if (!card) return;
      const tier = (card.getAttribute('data-tier') || '').toLowerCase();

      const isLow  = tier.indexOf('alap') === 0 || tier.indexOf('standard') === 0 || tier.indexOf('pro') === 0;
      const isHigh = tier.indexOf('elite') === 0 || tier.indexOf('premium') === 0 || tier.indexOf('prémium') === 0;

      if (isLow || isHigh) {
        e.preventDefault();
        // Tier-flag a modálra, hogy a CSS megkülönböztesse a warranty/accessory szekciókat
        const modal = root.querySelector('[data-ap-modal="upgrade"]');
        if (modal) {
          modal.setAttribute('data-current-tier', tier);
          modal.setAttribute('data-tier-group', isHigh ? 'high' : 'low');
        }
        if (root.modalAPI) root.modalAPI.open('upgrade');
      }
      // Else: native form submit proceeds to /cart/add
    });
  }

  /* ---------------------------------------------------------------------
     CURRENCY FORMATTER
     amount: integer (Shopify cents — minor units, except for HUF which is whole units)
     --------------------------------------------------------------------- */
  function formatMoney(amount, currency) {
    if (amount == null || isNaN(amount)) return '';
    const c = (currency || 'HUF').toUpperCase();
    let value;
    if (c === 'HUF') {
      // Shopify stores HUF in fillér (1/100) — convert
      value = Math.round(amount / 100);
      return formatNumber(value, 'hu-HU') + ' Ft';
    }
    value = amount / 100;
    if (c === 'EUR') return formatNumber(value, 'de-DE', 2) + ' €';
    if (c === 'RON') return formatNumber(value, 'ro-RO', 2) + ' lei';
    if (c === 'PLN') return formatNumber(value, 'pl-PL', 2) + ' zł';
    return formatNumber(value, 'en-US', 2) + ' ' + c;
  }
  function formatNumber(v, locale, decimals) {
    try {
      const opts = decimals != null
        ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
        : {};
      return new Intl.NumberFormat(locale, opts).format(v);
    } catch (e) {
      return String(v);
    }
  }

  // Expose for inline templates that may need it
  window.APMoney = formatMoney;
})();
